import { Typography, Image, Dropdown, Tag } from "antd";
import { MoreOutlined } from "@ant-design/icons";
import CustomButton from "../../../components/CustomButton/Button";
import EyeIcon from "../../../assets/icons/eye.svg";
import {
  formatCurrency,
  formatDate,
  formatBusinessDealStatus,
} from "../../../helpers/helpers";
import { GLOBAL_CONSTANTS } from "../../../constants/constants";
import { isSupplyChainCategory } from "../../../helpers/dealCategoryProducts";

const { Text } = Typography;

const textCell = (text, options = {}) => (
  <Text style={{ fontSize: 14, ...options.style }}>{text}</Text>
);

export const getDealsColumns = ({
  selectedStatus,
  dealCategoryId,
  navigate,
  onOpenReasonModal,
}) => {
  const isPendingOrRejected = ["pending", "rejected"].includes(selectedStatus);
  const isOngoingGroup = ["ongoing", "expired", "closed"].includes(
    selectedStatus,
  );

  const commonColumns = [
    {
      title: "Deal Id",
      dataIndex: "dealId",
      key: "dealId",
      fixed: "left",
      width: !isPendingOrRejected ? 100 : undefined,
      render: (text) =>
        textCell(text || GLOBAL_CONSTANTS.NA, { style: { fontWeight: 500 } }),
    },
    {
      title: "Enterprise Name",
      dataIndex: "entName",
      key: "entName",
      width: !isPendingOrRejected ? 300 : undefined,
      render: (text) => textCell(text || GLOBAL_CONSTANTS.NA),
    },
    {
      title: "Tenure",
      dataIndex: "tenure",
      key: "tenure",
      width: !isPendingOrRejected ? 100 : undefined,
      render: (text) => textCell(text || 0),
    },
    {
      title: isSupplyChainCategory(dealCategoryId) ? "Purchase Order Amount" : "Invoice Amount",
      dataIndex: "totalInvoiceAmt",
      key: "totalInvoiceAmt",
      width: !isPendingOrRejected ? 150 : undefined,
      render: (text) => textCell(formatCurrency(text || 0)),
    },
  ];

  const statusColumns = [];

  if (isPendingOrRejected) {
    statusColumns.push(
      {
        title: "Fund Required",
        dataIndex: "fundRequired",
        key: "fundRequired",
        width: 150,
        render: (text) => textCell(formatCurrency(text || 0)),
      },
      {
        title: "Comments",
        dataIndex: "comments",
        key: "comments",
        width: 200,
        render: (text) => textCell(text || GLOBAL_CONSTANTS.NA),
      },
    );
  }

  if (isOngoingGroup) {
    statusColumns.push(
      {
        title: "Deal Status",
        dataIndex: "dealStatus",
        key: "dealStatus",
        width: 150,
        render: (text) => textCell(formatBusinessDealStatus(text)),
      },
      {
        title: "Interest",
        dataIndex: "annualDiscountRate",
        key: "annualDiscountRate",
        width: 100,
        render: (text) => textCell(`${((text || 0) * 100).toFixed(2)}%`),
      },
      {
        title: "Approved Amount",
        dataIndex: "approvedAmt",
        key: "approvedAmt",
        width: 180,
        render: (text) => textCell(formatCurrency(text || 0)),
      },
      {
        title: "Funded Amount",
        dataIndex: "fundedAmt",
        key: "fundedAmt",
        width: 150,
        render: (text) => textCell(formatCurrency(text || 0)),
      },
      {
        title: "Disbursed Amount",
        dataIndex: "disbursedAmt",
        key: "disbursedAmt",
        width: 180,
        render: (text) => textCell(formatCurrency(text || 0)),
      },
      {
        title: "Repayment Date",
        dataIndex: "expectedRepaymentDate",
        key: "expectedRepaymentDate",
        width: 150,
        render: (text) =>
          textCell(text ? formatDate(text) : GLOBAL_CONSTANTS.NA),
      },
      {
        title: "Balance Amount",
        dataIndex: "balanceAmount",
        key: "balanceAmount",
        width: 150,
        render: (text) => textCell(formatCurrency(text || 0)),
      },
      {
        title: "Repaid Amount",
        dataIndex: "repaidAmt",
        key: "repaidAmt",
        width: 150,
        render: (text) => textCell(formatCurrency(text || 0)),
      },
      {
        title: "Repaid Date",
        dataIndex: "repaidDate",
        key: "repaidDate",
        width: 150,
        render: (text) =>
          textCell(text ? formatDate(text) : GLOBAL_CONSTANTS.NA),
      },
      {
        title: "Comments",
        dataIndex: "comments",
        key: "comments",
        width: 200,
        render: (text) => textCell(text || GLOBAL_CONSTANTS.NA),
      },
      {
        title: "Action",
        key: "action",
        fixed: "right",
        render: (_, row) => {
          const expectedDate = row?.expectedRepaymentDate
            ? new Date(row.expectedRepaymentDate)
            : null;

          const today = new Date();
          today.setHours(0, 0, 0, 0);

          const isOverdue = expectedDate ? expectedDate < today : false;
          const isDisbursedRepaid = row?.dealStatus === "disbursed" || row?.dealStatus === "repaid"
          return (
            <>
              <CustomButton
                type="text"
                icon={<Image preview={false} src={EyeIcon} alt="View" />}
                onClick={() =>
                  navigate("/dealDetailView", {
                    state: {
                      dealId: row.dealId,
                      selectedStatus,
                      dealStatusId: row.dealStatusId || row.statusId,
                      dealStatus: row.dealStatus || row.statusName,
                    },
                  })
                }
              />
              {isDisbursedRepaid && isOverdue && <Tag color="error">Overdue</Tag>}
            </>
          );
        },
      },
      ...(selectedStatus === "expired"
        ? [
            {
              title: "Raise Requests",
              key: "raiseRequest",
              fixed: "right",
              align: "center",
              width: 150,
              render: (_, row) => (
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: "republish",
                        label: "Republish",
                        onClick: () =>
                          onOpenReasonModal(row.dealId, "republish"),
                        disabled:
                          Number(row?.approvedAmt) === Number(row?.fundedAmt),
                      },
                      {
                        key: "cancel",
                        label: "Cancel",
                        onClick: () => onOpenReasonModal(row.dealId, "cancel"),
                      },
                      {
                        key: "disburse",
                        label: "Disburse",
                        onClick: () =>
                          onOpenReasonModal(row.dealId, "disburse"),
                      },
                    ],
                  }}
                  trigger={["click"]}
                >
                  <MoreOutlined style={{ fontSize: 20, cursor: "pointer" }} />
                </Dropdown>
              ),
            },
          ]
        : []),
    );
  }

  if (selectedStatus === "settled") {
    statusColumns.push(
      {
        title: "Settled Amount",
        dataIndex: "repaidAmt",
        key: "repaidAmt",
        width: 150,
        render: (text) => textCell(formatCurrency(text || 0)),
      },
      {
        title: "Settled Date",
        dataIndex: "settledDate",
        key: "settledDate",
        width: 150,
        render: (text) =>
          textCell(text ? formatDate(text) : GLOBAL_CONSTANTS.NA),
      },
      {
        title: "Comments",
        dataIndex: "comments",
        key: "comments",
        width: 200,
        render: (text) => textCell(text || GLOBAL_CONSTANTS.NA),
      },
      {
        title: "Action",
        key: "action",
        fixed: "right",
        render: (_, row) => (
          <CustomButton
            type="text"
            icon={<Image preview={false} src={EyeIcon} alt="View" />}
            onClick={() =>
              navigate("/dealDetailView", {
                state: {
                  dealId: row.dealId,
                  selectedStatus,
                  dealStatusId: row.dealStatusId || row.statusId,
                  dealStatus: row.dealStatus || row.statusName,
                },
              })
            }
          />
        ),
      },
    );
  }

  return [...commonColumns, ...statusColumns];
};
